import { useState, useEffect } from "react";
import {
  createUserWithEmailAndPassword,
  updateProfile,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  sendEmailVerification,
} from "firebase/auth";
import {
  doc,
  setDoc,
  serverTimestamp,
  getDoc,
  Timestamp,
} from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import { db, auth, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { EmailValidationBadge } from "../components/CreditCardTrialGate";
import { validateSchoolEmail, getTrialConfig } from "../services/schoolEmailValidation";

// Password strength calculator
const calculatePasswordStrength = (password) => {
  if (!password) return { strength: 0, label: '', color: '' };

  let strength = 0;
  if (password.length >= 6) strength += 20;
  if (password.length >= 10) strength += 20;
  if (/[a-z]/.test(password)) strength += 20;
  if (/[A-Z]/.test(password)) strength += 20;
  if (/[0-9]/.test(password)) strength += 10;
  if (/[^a-zA-Z0-9]/.test(password)) strength += 10;

  if (strength < 40) return { strength, label: 'Weak', color: 'bg-red-500' };
  if (strength < 70) return { strength, label: 'Fair', color: 'bg-yellow-500' };
  if (strength < 90) return { strength, label: 'Good', color: 'bg-blue-500' };
  return { strength, label: 'Strong', color: 'bg-green-500' };
};

/**
 * ✅ PR3: Server-side teacher profile initialization
 * Calls Cloud Function to create teacher profile with server-controlled trial
 *
 * SECURITY: Trial timestamps calculated server-side (prevents client clock manipulation)
 */
async function initializeTeacherProfile({ email, firstName, lastName, emailValidation, trialConfig }) {
  const initializeProfile = httpsCallable(functions, 'initializeTeacherProfile');

  try {
    const result = await initializeProfile({
      email,
      firstName,
      lastName,
      emailValidation: emailValidation || null,
      trialConfig: trialConfig || null
    });

    console.log('✅ Teacher profile initialized:', result.data);
    return result.data;
  } catch (error) {
    console.error('❌ Failed to initialize teacher profile:', error);
    throw error;
  }
}

export default function TeacherRegister() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ strength: 0, label: '', color: '' });
  const [fieldValidation, setFieldValidation] = useState({
    firstName: false,
    lastName: false,
    email: false,
    password: false
  });
  const [shake, setShake] = useState(false);
  // Removed step state - registration is now single-step
  const [emailValidation, setEmailValidation] = useState(null);
  const [trialConfig, setTrialConfig] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Real-time field validation
    if (name === 'firstName') {
      setFieldValidation(prev => ({ ...prev, firstName: value.trim().length > 0 }));
    }
    if (name === 'lastName') {
      setFieldValidation(prev => ({ ...prev, lastName: value.trim().length > 0 }));
    }
    if (name === 'email') {
      const isValid = /\S+@\S+\.\S+/.test(value);
      setFieldValidation(prev => ({ ...prev, email: isValid }));

      // Validate email as user types
      if (value.includes('@')) {
        const validation = validateSchoolEmail(value);
        const config = getTrialConfig(value);
        setEmailValidation(validation);
        setTrialConfig(config);
      }
    }
    if (name === 'password') {
      const strength = calculatePasswordStrength(value);
      setPasswordStrength(strength);
      setFieldValidation(prev => ({ ...prev, password: value.length >= 6 }));
    }
  };

  const validateForm = () => {
    const { firstName, lastName, email, password } = formData;

    if (!firstName.trim()) {
      setError("First name is required");
      triggerShake();
      return false;
    }

    if (!lastName.trim()) {
      setError("Last name is required");
      triggerShake();
      return false;
    }

    if (!email.trim()) {
      setError("Email is required");
      triggerShake();
      return false;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError("Please enter a valid email address");
      triggerShake();
      return false;
    }

    if (!password) {
      setError("Password is required");
      triggerShake();
      return false;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      triggerShake();
      return false;
    }

    return true;
  };

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };


  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateForm()) {
      return;
    }

    // ✅ NEW FLOW: Always create trial immediately (no card verification)
    await completeRegistrationWithTrial();
  };

  // ✅ OLD FUNCTIONS REMOVED: completeRegistrationPending, completeRegistrationPendingCard
  // All signups now use completeRegistrationWithTrial (no card verification)

  const completeRegistrationWithTrial = async () => {
    setLoading(true);

    try {
      const { user } = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      await updateProfile(user, { displayName: `${formData.firstName} ${formData.lastName}` });

      // 📧 Send email verification
      await sendEmailVerification(user);

      // Small delay to ensure auth state is fully propagated
      await new Promise(resolve => setTimeout(resolve, 100));

      console.log('Creating user profile for:', user.uid);

      // ✅ PR3: Call server function to initialize teacher profile with server-side trial
      await initializeTeacherProfile({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        emailValidation,
        trialConfig,
      });

      console.log('✅ Teacher profile created with server-side trial');

      // Wait a moment for AuthContext to load the new user document
      await new Promise(resolve => setTimeout(resolve, 500));

      // Navigate to dashboard (cleaner than window.location.href)
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err.code === "auth/email-already-in-use") {
        setError("Email already registered. Try logging in instead.");
      } else if (err.code === "permission-denied") {
        setError("Permission denied. Please try again or contact support.");
        console.error("Firestore permission denied:", err);
      } else {
        setError("Registration failed. Please try again.");
      }
      console.error("Registration error:", err);
    } finally {
      setLoading(false);
    }
  };

  // OAuth signup handlers
  const handleGoogleSignup = async () => {
    setOauthLoading('google');
    setError('');

    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('https://www.googleapis.com/auth/classroom.courses.readonly');
      provider.addScope('https://www.googleapis.com/auth/classroom.rosters.readonly');
      provider.addScope('https://www.googleapis.com/auth/classroom.profile.emails');
      provider.addScope('https://www.googleapis.com/auth/classroom.profile.photos');

      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const credential = GoogleAuthProvider.credentialFromResult(result);

      await createOAuthUser(user, credential, 'google', 'Google Classroom');
    } catch (err) {
      console.error('Google signup error:', err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Signup was cancelled. Please try again.');
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        setError('An account already exists with this email using a different signup method.');
      } else {
        setError('Google signup failed. Please try again or use email signup.');
      }
    } finally {
      setOauthLoading(null);
    }
  };

  const handleMicrosoftSignup = async () => {
    setOauthLoading('microsoft');
    setError('');

    try {
      const provider = new OAuthProvider('microsoft.com');
      provider.addScope('https://graph.microsoft.com/EduRoster.Read');
      provider.addScope('https://graph.microsoft.com/Team.ReadBasic.All');
      provider.addScope('https://graph.microsoft.com/User.Read');
      provider.addScope('https://graph.microsoft.com/TeamMember.Read.All');

      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const credential = OAuthProvider.credentialFromResult(result);

      await createOAuthUser(user, credential, 'microsoft', 'Microsoft Teams');
    } catch (err) {
      console.error('Microsoft signup error:', err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Signup was cancelled. Please try again.');
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        setError('An account already exists with this email using a different signup method.');
      } else {
        setError('Microsoft signup failed. Please try again or use email signup.');
      }
    } finally {
      setOauthLoading(null);
    }
  };

  const createOAuthUser = async (user, credential, provider, providerName) => {
    try {
      // Check if user document already exists
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        // User already exists, redirect to dashboard
        navigate('/dashboard');
        return;
      }

      // Validate email and get trial config
      const emailValidation = validateSchoolEmail(user.email);
      const trialConfig = getTrialConfig(user.email);

      // Generate teacher ID from name
      const displayName = user.displayName || user.email.split('@')[0];
      const nameParts = displayName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // ✅ PR3: Call server function to initialize teacher profile with server-side trial
      await initializeTeacherProfile({
        email: user.email,
        firstName,
        lastName,
        emailValidation,
        trialConfig,
      });

      // Store OAuth token for API access
      if (credential && credential.accessToken) {
        await setDoc(doc(db, 'userTokens', user.uid), {
          [`${provider}AccessToken`]: credential.accessToken,
          [`${provider}RefreshToken`]: credential.refreshToken || null,
          [`${provider}TokenExpiresAt`]: credential.expirationTime || Date.now() + 3600000,
          updatedAt: serverTimestamp()
        }, { merge: true });
      }

      console.log(`${providerName} signup successful for:`, user.email);

      // Navigate to dashboard
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error(`Error creating ${providerName} user:`, err);
      setError(`Failed to complete ${providerName} signup. Please try again.`);
    }
  };

  // Registration is now single-step - card verification moved to separate page

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center px-4 py-8 sm:py-12">
      <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-10 w-full max-w-lg">
        {/* Trust Line */}
        <div className="flex justify-center items-center mb-6">
          <p className="text-xs text-gray-600">
            Private beta · Browser-based secure testing · Built for GCC schools
          </p>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-4xl sm:text-5xl font-extrabold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-3">
            Start using ExamLock for free
          </h1>
          <p className="text-base sm:text-lg text-gray-600 mb-2">
            Create AI-powered assessments in minutes - no credit card required
          </p>
          <p className="text-sm text-gray-500">
            Join teachers in private beta using ExamLock
          </p>
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 mt-5 shadow-sm">
            <p className="text-green-800 font-bold text-sm sm:text-base">
              ✨ Free Beta • Up to {emailValidation?.isSchool ? 50 : 10} students • Limited AI generation
            </p>
          </div>
        </div>

        {error && (
          <div className={`bg-red-100 border-2 border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6 ${shake ? 'animate-shake' : ''}`}>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}

        {/* OAuth Signup Options - Temporarily hidden until verification complete */}
        {/* TODO: Re-enable after completing Google/Microsoft OAuth verification */}
        {false && (
        <div className="space-y-3 mb-6">
          <button
            type="button"
            onClick={handleGoogleSignup}
            disabled={oauthLoading !== null}
            className="w-full flex items-center justify-center px-4 py-4 border-2 border-gray-200 rounded-xl shadow-md bg-white text-gray-700 hover:bg-gray-50 hover:shadow-lg hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
          >
            {oauthLoading === 'google' ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                <span>Connecting...</span>
              </div>
            ) : (
              <>
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="font-medium">Sign up with Google Classroom</span>
              </>
            )}
          </button>

          <button
            type="button"
            onClick={handleMicrosoftSignup}
            disabled={oauthLoading !== null}
            className="w-full flex items-center justify-center px-4 py-4 border-2 border-gray-200 rounded-xl shadow-md bg-white text-gray-700 hover:bg-gray-50 hover:shadow-lg hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
          >
            {oauthLoading === 'microsoft' ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                <span>Connecting...</span>
              </div>
            ) : (
              <>
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#f25022" d="M1 1h10v10H1z"/>
                  <path fill="#00a4ef" d="M13 1h10v10H13z"/>
                  <path fill="#7fba00" d="M1 13h10v10H1z"/>
                  <path fill="#ffb900" d="M13 13h10v10H13z"/>
                </svg>
                <span className="font-medium">Sign up with Microsoft Teams</span>
              </>
            )}
          </button>
        </div>
        )}

        {/* Divider */}
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Or sign up with email</span>
          </div>
        </div>

        <form onSubmit={handleFormSubmit} className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-semibold mb-2">
                First Name
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-10 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base transition-all"
                  placeholder="Jane"
                  required
                  disabled={loading}
                />
                {fieldValidation.firstName && formData.firstName && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-semibold mb-2">
                Last Name
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-10 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base transition-all"
                  placeholder="Smith"
                  required
                  disabled={loading}
                />
                {fieldValidation.lastName && formData.lastName && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2">
              Email Address
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full pl-10 pr-10 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base transition-all"
                placeholder="your.email@school.edu"
                required
                disabled={loading}
              />
              {fieldValidation.email && formData.email && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
            {emailValidation && formData.email && (
              <div className="mt-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg animate-fade-in">
                <div className="flex items-center justify-between">
                  <EmailValidationBadge email={formData.email} />
                  {trialConfig && (
                    <div className="flex items-center gap-1 text-xs font-semibold">
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full">✅ No Card Needed</span>
                    </div>
                  )}
                </div>
                {trialConfig && (
                  <p className="text-xs text-blue-700 mt-2 font-medium">
                    🎉 School email detected! Get {trialConfig.trialDays} days free + {trialConfig.studentsLimit || 50} student limit
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2 flex items-center gap-2">
              Password
              <span className="text-xs text-gray-500">(minimum 6 characters)</span>
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full pl-10 pr-12 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base transition-all"
                placeholder="Choose a secure password"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </button>
            </div>
            {/* Password Strength Meter */}
            {formData.password && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600">Password Strength:</span>
                  <span className={`text-xs font-semibold ${
                    passwordStrength.strength < 40 ? 'text-red-600' :
                    passwordStrength.strength < 70 ? 'text-yellow-600' :
                    passwordStrength.strength < 90 ? 'text-blue-600' : 'text-green-600'
                  }`}>
                    {passwordStrength.label}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                    style={{ width: `${passwordStrength.strength}%` }}
                  ></div>
                </div>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Minimum 6 characters. Use a mix of letters, numbers, and symbols for better security.
            </p>
          </div>

          {/* Enhanced "What You'll Get" Section with Cards */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5">
            <p className="text-center font-bold text-gray-800 mb-4 text-base">🎉 What You'll Get Instantly:</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-white rounded-lg p-3 text-center shadow-sm hover:shadow-md transition-shadow">
                <div className="text-3xl mb-2">✨</div>
                <div className="text-xs font-semibold text-gray-800">AI Exam Creator</div>
              </div>
              <div className="bg-white rounded-lg p-3 text-center shadow-sm hover:shadow-md transition-shadow">
                <div className="text-3xl mb-2">🔒</div>
                <div className="text-xs font-semibold text-gray-800">Anti-Cheat Security</div>
              </div>
              <div className="bg-white rounded-lg p-3 text-center shadow-sm hover:shadow-md transition-shadow">
                <div className="text-3xl mb-2">📊</div>
                <div className="text-xs font-semibold text-gray-800">Real-Time Monitoring</div>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || oauthLoading !== null}
            className={`w-full py-5 px-6 rounded-xl font-bold transition-all duration-300 text-lg shadow-lg ${
              loading || oauthLoading !== null
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
            }`}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-2"></div>
                Creating Account...
              </div>
            ) : (
              <span className="flex items-center justify-center gap-2">
                🚀 Start My Free Trial
              </span>
            )}
          </button>
        </form>

        <div className="mt-4 sm:mt-6 text-center space-y-3">
          <p className="text-xs text-gray-500">
            By signing up, you agree to our Terms of Service and Privacy Policy
          </p>
          <div className="space-y-2">
            <Link 
              to="/login" 
              className="text-blue-600 hover:text-blue-800 text-sm block font-medium"
            >
              Already have an account? Sign in →
            </Link>
            <Link 
              to="/" 
              className="text-gray-500 hover:text-gray-700 text-sm block"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}